// src/main/content/contests/ContestsDisplay.js
//
// Top-level container for the Contests tab. Owns:
//   - Fetching the contest list
//   - Header (title + "New Contest" button)
//   - Status subtabs (Live / Upcoming / Completed / All)
//   - Filter dropdowns (Type, Metric, Stores) + search
//   - Routing between list / detail / create-edit form
//
// Layout mirrors the maintenance tickets page so an admin with hundreds of
// contests across many stores can narrow down quickly. All filtering happens
// client-side against the already-loaded contest list — the backend hasn't
// gained list-filter parameters yet and the contest payload is small enough
// (one row per contest, no per-row aggregates) that this is fine.
//
// Permission model:
//   - Anyone with `contests_enabled` at one of their stores can land here.
//   - Admins/managers see the "New Contest" button and edit affordance;
//     backend gates further by per-store membership (`isAdmin` is used as a
//     display flag — backend is source of truth).

import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";

import ContestList from "./ContestList";
import ContestDetails from "./ContestDetails";
import ContestFormBuilder from "./ContestFormBuilder";
import LoadSpinner from "../../../utilities/LoadSpinner";
import SubTabs from "../home/top_bar_components/SubTabs";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_contests } from "../../../constants";

// Status subtabs — same options as before; "all" disables the status filter.
// "drafts" is orthogonal to date-status: drafts are excluded from the three
// status tabs (a draft isn't really Live), included in All, and get their
// own tab. Only rendered for manager-or-higher — employees never receive
// drafts from the backend anyway.
const TABS = [
  { key: "live", label: "Live", icon: BoltIcon, status: "Active" },
  { key: "upcoming", label: "Upcoming", icon: ScheduleIcon, status: "Upcoming" },
  { key: "completed", label: "Completed", icon: CheckCircleIcon, status: "Completed" },
  { key: "drafts", label: "Drafts", icon: EditNoteIcon, status: "drafts", adminOnly: true },
  { key: "all", label: "All", icon: EmojiEventsIcon, status: "all" },
];

// Type filter options — kept as a multi-select for consistency with the
// other filters even though there are only two values. The "All" state is
// represented by `typeFilter.length === 0`.
const TYPE_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "store_vs_store", label: "Store vs Store" },
];

// Shared sx for the filter <Select> components. Matches the maintenance
// tickets page so the two pages feel visually consistent.
const SELECT_SX = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "40px",
  fontSize: "14px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

const CHECKBOX_SX = {
  color: "#6d28d9",
  "&.Mui-checked": { color: "#6d28d9" },
};

// Compact "All Xs" / "Foo, Bar" / "Foo, +N more" label for the multi-select
// trigger. Mirrors the maintenance helper.
function renderMultiSelectValue(selected, options, allLabel) {
  if (selected.length === 0 || selected.length === options.length) {
    return allLabel;
  }
  const labels = options
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);
  if (labels.length <= 2) return labels.join(", ");
  return `${labels[0]}, +${labels.length - 1} more`;
}

export default function ContestsDisplay({
  permissionRole,
  homeStore,
  homeCompany,
  storeOptions: membershipOptions,
  storeNames,
  change_home_store,
  username,
  stmId,
}) {
  const [activeTab, setActiveTab] = useState("live");
  const [contests, setContests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);

  // Filter state. All multi-select; empty = no filter (= "All"). The status
  // subtab is its own state (activeTab) because it's a single-select.
  const [typeFilter, setTypeFilter] = useState([]);
  const [metricFilter, setMetricFilter] = useState([]);
  const [storeFilter, setStoreFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Managers can now create/edit/delete contests, gated by the backend to
  // stores they actually belong to. Admins/rightwork_internal are
  // unrestricted. The FE keeps the variable name `isAdmin` so the prop
  // contract with children (which render an "edit" affordance when true)
  // doesn't churn.
  const isAdmin =
    permissionRole === "rightwork_internal" ||
    permissionRole === "company_admin" ||
    permissionRole === "company_manager";

  const activeStatusFilter =
    TABS.find((t) => t.key === activeTab)?.status || "all";

  useEffect(() => {
    fetchContests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeStore]);

  // Live mirror of homeStore: the header company selector can change the
  // store while a fetch is in flight, and getRightworkAPI neither cancels
  // nor tags requests, so a slower response for the previous company must
  // not land on top of the new one.
  const homeStoreRef = useRef(homeStore);
  useEffect(() => {
    homeStoreRef.current = homeStore;
  }, [homeStore]);

  const fetchContests = async (showLoading = true) => {
    const requestStore = homeStore;
    const isCurrent = () => homeStoreRef.current === requestStore;
    if (showLoading) setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_contests, { store_id: requestStore });
      if (!isCurrent()) return;
      if (res.status === 200) {
        setContests(res.data.redux.payload.contests || []);
      } else {
        throw new Error(res.data.system?.message || "Failed to load contests");
      }
    } catch (err) {
      if (!isCurrent()) return;
      toast.error(`Error loading contests: ${err.message}`, { autoClose: 3000 });
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  };

  // Header company selector: only companies with Contests enabled somewhere.
  // The sidebar hides Contests per store, but nothing blocks the tab once
  // open, so an unfiltered selector would let a user hop into a company
  // where the feature is off. Filtered by COMPANY (the full storeNames list
  // still goes to the header) so the anchor-store choice can still prefer a
  // manager membership in the target company.
  const contestCompanyIds = useMemo(
    () => [
      ...new Set(
        (storeNames || []).filter((s) => s.contests_enabled).map((s) => s.company)
      ),
    ],
    [storeNames]
  );

  // Derive store / metric options from the loaded contests rather than from
  // ``storeNames`` or a separate metrics fetch. This way the dropdowns only
  // surface options that will actually yield results — clicking any option
  // is guaranteed to leave at least one card visible.
  const storeOptions = useMemo(() => {
    const map = new Map();
    (contests || []).forEach((c) => {
      const ids = c.store_ids || [];
      const names = c.store_names || [];
      ids.forEach((id, idx) => {
        if (id != null && !map.has(id)) {
          map.set(id, { id, store_name: names[idx] || `Store ${id}` });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.store_name.localeCompare(b.store_name)
    );
  }, [contests]);

  const metricOptions = useMemo(() => {
    const map = new Map();
    (contests || []).forEach((c) => {
      if (c.metric_key && !map.has(c.metric_key)) {
        map.set(c.metric_key, {
          value: c.metric_key,
          label: c.metric_label || c.metric_key,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [contests]);

  // Prune filter selections when the underlying options change (e.g. the
  // user navigates between companies and the available stores shift). Stale
  // IDs in the filter state would silently exclude every contest.
  useEffect(() => {
    setStoreFilter((prev) => {
      const valid = new Set(storeOptions.map((o) => o.id));
      const pruned = prev.filter((id) => valid.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [storeOptions]);

  useEffect(() => {
    setMetricFilter((prev) => {
      const valid = new Set(metricOptions.map((o) => o.value));
      const pruned = prev.filter((k) => valid.has(k));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [metricOptions]);

  // Multi-select change handler with built-in "Select / Deselect all"
  // toggling via the sentinel "__all__" menu item.
  const handleMultiSelectChange = (setter, allOptionValues) => (event) => {
    const { value } = event.target;
    const arr = Array.isArray(value) ? value : [value];
    if (arr.includes("__all__")) {
      setter((prev) =>
        prev.length === allOptionValues.length ? [] : [...allOptionValues]
      );
      return;
    }
    setter(arr);
  };

  // Final filtered list — feeds ContestList. Order of operations is the
  // same as maintenance: status → categorical filters → search. Search is
  // last because string ops are the most expensive.
  const filteredContests = useMemo(() => {
    let data = contests || [];
    if (activeStatusFilter === "drafts") {
      data = data.filter((c) => c.is_draft);
    } else if (activeStatusFilter !== "all") {
      // Drafts live only in their own tab (and All) — a draft whose dates
      // happen to be current isn't "Live".
      data = data.filter(
        (c) => c.status === activeStatusFilter && !c.is_draft
      );
    }
    if (typeFilter.length > 0 && typeFilter.length < TYPE_OPTIONS.length) {
      data = data.filter((c) => typeFilter.includes(c.contest_type));
    }
    if (metricFilter.length > 0 && metricFilter.length < metricOptions.length) {
      data = data.filter((c) => metricFilter.includes(c.metric_key));
    }
    if (storeFilter.length > 0 && storeFilter.length < storeOptions.length) {
      const sel = new Set(storeFilter);
      data = data.filter((c) =>
        (c.store_ids || []).some((id) => sel.has(id))
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q) ||
          (c.metric_label || "").toLowerCase().includes(q)
      );
    }
    return data;
  }, [
    contests,
    activeStatusFilter,
    typeFilter,
    metricFilter,
    storeFilter,
    searchQuery,
    metricOptions.length,
    storeOptions.length,
  ]);

  // "Restrictive" means non-empty AND not fully selected — a filter where
  // every option is checked is functionally the same as no filter (the
  // filter pass above explicitly skips it), so we shouldn't show the
  // "filters are hiding everything" empty state in that case.
  const isRestrictive = (selected, allOptions) =>
    selected.length > 0 && selected.length < allOptions.length;
  const dropdownFiltersActive =
    isRestrictive(typeFilter, TYPE_OPTIONS) ||
    isRestrictive(metricFilter, metricOptions) ||
    isRestrictive(storeFilter, storeOptions) ||
    !!searchQuery.trim();

  // Status-aware empty-state config. We branch on dropdown filters first
  // (they're the most-likely culprit when an admin can't find a contest),
  // then on the status tab so each tab gets a tailored message + the
  // appropriate CTA. The Create button stays available wherever it makes
  // sense — every tab except Completed (you can't create a contest that's
  // already over).
  const emptyState = useMemo(() => {
    if (dropdownFiltersActive) {
      return {
        title: "No contests match these filters",
        message: "Try clearing a filter or switching tabs.",
        showCreate: false,
      };
    }
    if (activeStatusFilter === "Active") {
      return {
        title: "No active contests right now",
        message: isAdmin
          ? "Kick one off to fire up the leaderboard."
          : "Check back once a contest is launched.",
        showCreate: true,
      };
    }
    if (activeStatusFilter === "Upcoming") {
      return {
        title: "Nothing scheduled yet",
        message: isAdmin
          ? "Schedule a contest to start the countdown."
          : "Check back once a contest is launched.",
        showCreate: true,
      };
    }
    if (activeStatusFilter === "Completed") {
      return {
        title: "No completed contests yet",
        message: "Wrapped contests will show up here.",
        showCreate: false,
      };
    }
    if (activeStatusFilter === "drafts") {
      return {
        title: "No drafts yet",
        message: "Save a contest as a draft to finish or preview it before employees can see it.",
        showCreate: true,
      };
    }
    return {
      title: "No contests yet",
      message: isAdmin
        ? "Create one to get the leaderboard rolling."
        : "Check back once a contest is launched.",
      showCreate: true,
    };
  }, [dropdownFiltersActive, activeStatusFilter, isAdmin]);

  const handleCreate = () => {
    setSelectedContest(null);
    setWizardOpen(true);
  };

  const handleEdit = (c) => {
    setSelectedContest(c);
    setWizardOpen(true);
  };

  const handleSelect = (c) => {
    setSelectedContest(c);
    setWizardOpen(false);
  };

  const handleWizardClose = (refresh) => {
    // Clear selectedContest BEFORE wizardOpen. The app is still on the legacy
    // ReactDOM.render path, so post-await setState calls aren't auto-batched
    // — each setState commits its own render. If wizardOpen flipped to false
    // first, there'd be an intermediate render with (selectedContest=C,
    // wizardOpen=false), which matches the ContestDetails guard below and
    // briefly remounts the detail view for a just-deleted contest, firing a
    // "Error loading leaderboard" toast from its mount-effect fetch.
    setSelectedContest(null);
    setWizardOpen(false);
    if (refresh) fetchContests(false);
  };

  const handleBackToList = (refresh = false) => {
    setSelectedContest(null);
    setWizardOpen(false);
    if (refresh) fetchContests(false);
  };

  // Detail view — intentionally skips the inline header for this route. The
  // detail page is information-dense already; reclaiming that vertical space
  // lets the leaderboard / chart / replay show more without scrolling.
  // ContestDetails has its own back button at the top.
  if (selectedContest && !wizardOpen) {
    return (
      <div className="w-full">
        <ContestDetails
          contest={selectedContest}
          homeStore={homeStore}
          storeNames={storeNames}
          username={username}
          stmId={stmId}
          isAdmin={isAdmin}
          onBack={handleBackToList}
          onEdit={handleEdit}
        />
      </div>
    );
  }

  if (wizardOpen) {
    return (
      <div className="w-full">
        <ContestFormBuilder
          contest={selectedContest}
          homeStore={homeStore}
          storeNames={storeNames}
          stmId={stmId}
          onClose={handleWizardClose}
        />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-white">
      {/* Header */}
      <div className="my-5">
      <OperationsPageHeader
        title="Contests"
        scope="company"
        homeStore={homeStore}
        homeCompany={homeCompany}
        storeOptions={membershipOptions}
        storeNames={storeNames}
        eligibleCompanyIds={contestCompanyIds}
        change_home_store={change_home_store}
      >
        {/* Status subtabs (shared SubTabs, shrink-to-tabs underline) with
            New Contest on the same row, like Tickets. */}
        <SubTabs
          className="mt-4"
          tabs={TABS.filter((t) => !t.adminOnly || isAdmin)}
          activeTab={activeTab}
          onChange={setActiveTab}
          rightSlot={
            isAdmin && (
              <button
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
                onClick={handleCreate}
              >
                <AddIcon fontSize="small" />
                New Contest
              </button>
            )
          }
        />

        {/* Filter row.
            Hidden until contests have loaded — the Metric and Stores
            dropdowns are derived from the loaded contest set, so during
            the initial fetch only Type and Search would render and the
            row would visibly jump from 2 to 4 elements once data lands.
            Showing the whole row at once after loading avoids that
            reflow without needing skeleton placeholders. */}
        {!isLoading && (
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          {/* Type filter */}
          <div className="flex-1 min-w-[140px]">
            <Select
              multiple
              value={typeFilter}
              onChange={handleMultiSelectChange(
                setTypeFilter,
                TYPE_OPTIONS.map((o) => o.value)
              )}
              input={<OutlinedInput />}
              renderValue={() =>
                renderMultiSelectValue(typeFilter, TYPE_OPTIONS, "All Types")
              }
              displayEmpty
              size="small"
              sx={SELECT_SX}
              className="w-full"
            >
              <MenuItem value="__all__">
                <Checkbox
                  checked={typeFilter.length === TYPE_OPTIONS.length}
                  sx={CHECKBOX_SX}
                />
                <ListItemText
                  primary={
                    typeFilter.length === TYPE_OPTIONS.length
                      ? "Deselect all"
                      : "All Types"
                  }
                  primaryTypographyProps={{ style: { fontWeight: 600 } }}
                />
              </MenuItem>
              {TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox
                    checked={typeFilter.includes(opt.value)}
                    sx={CHECKBOX_SX}
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* Metric filter — auto-hides when there's only one distinct
              metric across the loaded contests (a filter with one option is
              just noise). */}
          {metricOptions.length > 1 && (
            <div className="flex-1 min-w-[180px]">
              <Select
                multiple
                value={metricFilter}
                onChange={handleMultiSelectChange(
                  setMetricFilter,
                  metricOptions.map((o) => o.value)
                )}
                input={<OutlinedInput />}
                renderValue={() =>
                  renderMultiSelectValue(metricFilter, metricOptions, "All Metrics")
                }
                displayEmpty
                size="small"
                sx={SELECT_SX}
                className="w-full"
              >
                <MenuItem value="__all__">
                  <Checkbox
                    checked={metricFilter.length === metricOptions.length}
                    sx={CHECKBOX_SX}
                  />
                  <ListItemText
                    primary={
                      metricFilter.length === metricOptions.length
                        ? "Deselect all"
                        : "All Metrics"
                    }
                    primaryTypographyProps={{ style: { fontWeight: 600 } }}
                  />
                </MenuItem>
                {metricOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox
                      checked={metricFilter.includes(opt.value)}
                      sx={CHECKBOX_SX}
                    />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}

          {/* Store filter — same auto-hide rule. */}
          {storeOptions.length > 1 && (
            <div className="flex-1 min-w-[180px]">
              <Select
                multiple
                value={storeFilter}
                onChange={handleMultiSelectChange(
                  setStoreFilter,
                  storeOptions.map((o) => o.id)
                )}
                input={<OutlinedInput />}
                renderValue={() => {
                  if (
                    storeFilter.length === 0 ||
                    storeFilter.length === storeOptions.length
                  ) {
                    return "All Stores";
                  }
                  const names = storeOptions
                    .filter((s) => storeFilter.includes(s.id))
                    .map((s) => s.store_name);
                  if (names.length <= 2) return names.join(", ");
                  return `${names[0]}, +${names.length - 1} more`;
                }}
                displayEmpty
                size="small"
                sx={SELECT_SX}
                className="w-full"
              >
                <MenuItem value="__all__">
                  <Checkbox
                    checked={storeFilter.length === storeOptions.length}
                    sx={CHECKBOX_SX}
                  />
                  <ListItemText
                    primary={
                      storeFilter.length === storeOptions.length
                        ? "Deselect all"
                        : "All Stores"
                    }
                    primaryTypographyProps={{ style: { fontWeight: 600 } }}
                  />
                </MenuItem>
                {storeOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox
                      checked={storeFilter.includes(s.id)}
                      sx={CHECKBOX_SX}
                    />
                    <ListItemText primary={s.store_name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}

          {/* Search — matches contest name, description, or metric label. */}
          <div className="flex-1 min-w-[200px]">
            <TextField
              fullWidth
              placeholder="Search contests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                  />
                ),
                endAdornment: searchQuery && (
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontSize: "14px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
          </div>
        </div>
        )}
      </OperationsPageHeader>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadSpinner />
        </div>
      ) : (
        <div className="pt-4">
          <ContestList
            contests={filteredContests}
            isAdmin={isAdmin}
            onEdit={handleEdit}
            onSelect={handleSelect}
            onCreate={handleCreate}
            emptyState={emptyState}
          />
        </div>
      )}
    </div>
  );
}
